import { useTheme } from '@emotion/react'
import { AppState } from '@news-mono/web-common'
import React from 'react'
import { useSelector } from 'react-redux'
import {
    OnSubscribeEventHandler,
    useAuthActions,
} from '../../user-registration/authentication/AuthenticationProvider'
import { THEWEST_ALL } from '../../user-registration/entitlements/common'
import { createSubscribeLinkClickEvent } from '../../user-registration/subscribe/createSubscribeClickLinkHandler'
import { LogoutActionLink } from './LogoutActionLink'
import { RegionalActionLink } from './RegionalActionLink'
import {
    StyledActionButton,
    StyledActionLinks,
    StyledPremiumIcon,
    StyledSkeleton,
    subscribeButtonClassname,
} from './TheWestHeaderLogin.styled'
import { UserName, UserNameLink } from './UserNameLink'

export interface AuthSiteLoginActionLinksProps {
    onEvent: (event: any) => void
}

export const AuthSiteLoginActionLinks: React.FC<AuthSiteLoginActionLinksProps> =
    ({ onEvent }) => {
        const {
            userName,
            isLoggedIn,
            hasAttemptedValidation,
            entitlements,
            subscriptionType,
        } = useSelector((state: AppState) => state.authentication)
        const { section } = useTheme()
        const isRegionalSite = section !== 'default'
        const { onLoginClick, onSubscribeClick, onLogoutClick } =
            useAuthActions()

        /**
         * Check to see if the user likely has an account on SSW before we link there,
         * Regwall users only have `thewest:registered`
         */
        const showMyAccountLink =
            isLoggedIn &&
            entitlements &&
            entitlements.includes(THEWEST_ALL) &&
            subscriptionType !== 'corporate-site'

        return isLoggedIn ? (
            <ActionLinksAuthenticated
                isRegionalSite={isRegionalSite}
                onLogoutClick={() => onLogoutClick(onEvent)}
                userName={userName}
                hasAccount={showMyAccountLink}
            />
        ) : (
            <ActionLinksUnauthenticated
                onLoginClick={onLoginClick}
                onSubscribeClick={onSubscribeClick}
                isRegionalSite={isRegionalSite}
                hasAttemptedValidation={hasAttemptedValidation}
                onEvent={onEvent}
            />
        )
    }

export interface ActionLinksUnauthenticatedProps {
    isRegionalSite: boolean
    onLoginClick: () => void
    onSubscribeClick: OnSubscribeEventHandler
    hasAttemptedValidation: boolean
    onEvent: (event: any) => void
}

export const ActionLinksUnauthenticated: React.FC<ActionLinksUnauthenticatedProps> =
    ({
        isRegionalSite,
        hasAttemptedValidation,
        onLoginClick,
        onSubscribeClick,
        onEvent,
    }) => {
        return (
            <StyledActionLinks>
                {isRegionalSite && <RegionalActionLink />}

                <StyledActionButton isPrimary={false} onClick={onLoginClick}>
                    {hasAttemptedValidation ? (
                        'Login'
                    ) : (
                        <StyledSkeleton count={1} />
                    )}
                </StyledActionButton>

                <StyledActionButton
                    className={subscribeButtonClassname}
                    onClick={() => {
                        createSubscribeLinkClickEvent('header', onEvent)()
                        onSubscribeClick({
                            callToAction: 'header',
                        })
                    }}
                    isPrimary={true}
                >
                    {hasAttemptedValidation ? (
                        <React.Fragment>
                            <StyledPremiumIcon />
                            Subscribe
                        </React.Fragment>
                    ) : (
                        <StyledSkeleton count={1} />
                    )}
                </StyledActionButton>
            </StyledActionLinks>
        )
    }

export interface ActionLinksAuthenticatedProps {
    isRegionalSite: boolean
    userName: string
    hasAccount: boolean
    onLogoutClick: () => void
}

export const ActionLinksAuthenticated: React.FC<ActionLinksAuthenticatedProps> =
    ({ isRegionalSite, userName, hasAccount, onLogoutClick }) => (
        <StyledActionLinks>
            {isRegionalSite && <RegionalActionLink />}
            {hasAccount ? (
                <UserNameLink userName={userName} />
            ) : (
                <UserName userName={userName} />
            )}
            <LogoutActionLink onLogoutClick={onLogoutClick} />
        </StyledActionLinks>
    )
